import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Wind, Salad, TreePine, Waves, Mountain } from "lucide-react";
import { Reveal, Overline, SectionHeading } from "@/components/Primitives";
import { InnerHero, CTABand } from "@/components/Sections";
import { IMG } from "@/data/site";

const PILLARS = [
  { icon: Leaf, title: "Ayurveda Healing Retreats", text: "Restorative rituals and traditional oils in serene, unhurried settings." },
  { icon: Wind, title: "Yoga & Meditation", text: "Sunrise practice by the sea or in cool hill-station calm." },
  { icon: Salad, title: "Nutrition & Traditional Food", text: "Sattvic, seasonal cuisine rooted in age-old local tradition." },
  { icon: TreePine, title: "Nature Therapy", text: "Forest walks, plantation air and the slow pace of the Ghats." },
  { icon: Waves, title: "Beachside Wellness Stays", text: "Ocean-facing resorts designed for deep relaxation." },
  { icon: Mountain, title: "Mountain Wellness Escapes", text: "Misty retreats amid tea gardens and quiet valleys." },
];

export default function Wellness() {
  return (
    <>
      <InnerHero
        overline="Wellness & Ayurveda"
        title="Rest, restore, return renewed"
        subtitle="A calmer way to travel — Ayurveda, yoga and nature-led rejuvenation across India's most peaceful settings."
        image={IMG.ayurvedaSpa}
      />

      {/* intro split */}
      <section className="bg-ivory py-24 md:py-32" data-testid="wellness-intro">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden h-[460px]">
              <img src={IMG.ayurvedaPool} alt="Wellness resort" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline className="text-gold">A relaxation-led approach</Overline>
            <h2 className="mt-4 font-serif font-normal text-4xl md:text-5xl leading-[1.05] tracking-tight text-forest">
              Wellness as a way of living, not a treatment
            </h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-navy/70">
              We present Ayurveda as relaxation, rejuvenation and a traditional lifestyle
              experience — never as a medical claim. Think slow mornings, nourishing food,
              gentle movement and time in nature, all within premium, calming surroundings.
            </p>
            <p className="mt-4 text-navy/60 font-light leading-relaxed">
              Every retreat is chosen for its atmosphere, its setting and the quiet quality of
              its hospitality — so guests leave genuinely rested.
            </p>
          </Reveal>
        </div>
      </section>

      {/* pillars */}
      <section className="bg-sand/30 py-24 md:py-32" data-testid="wellness-pillars">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><SectionHeading overline="Focus areas" title="Curated wellness experiences" align="center" className="mx-auto" /></Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-sand bg-ivory p-8" data-testid={`wellness-pillar-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center text-teal">
                    <p.icon size={22} />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-forest">{p.title}</h3>
                  <p className="mt-3 text-sm text-navy/65 font-light leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* quiet quote band */}
      <section className="bg-forest text-ivory grain-dark relative">
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-10 py-24 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="font-serif text-3xl md:text-4xl font-light leading-snug"
          >
            “Luxury wellness travel, at the pace of the coast — authentic yet comfortable,
            premium yet deeply calm.”
          </motion.p>
        </div>
      </section>

      <CTABand
        overline="Plan a wellness retreat"
        title="Design a restorative India escape"
        primary={{ label: "Enquire About Wellness", to: "/contact" }}
        secondary={{ label: "View Experiences", to: "/experiences" }}
      />
    </>
  );
}
